import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import strings from '../../../../../common/Translation/Translate';
import { VenueConfigEmptyState } from '../VenueConfigEmptyState';

export type VenueConfigAddOnItem = {
  id: number;
  name: string;
  addOnCategory: string;
  addOnCategoryName?: string;
};

type CategoryGroup = {
  code: string;
  name: string;
  items: VenueConfigAddOnItem[];
};

type CategorizedAddonSelectorProps = {
  addOnItems: VenueConfigAddOnItem[];
  selectedIds: number[];
  onChange: (selectedIds: number[]) => void;
};

const getCategoryKey = (item: VenueConfigAddOnItem) => item.addOnCategory || 'OTHER';

const getCategoryLabel = (item: VenueConfigAddOnItem) =>
  item.addOnCategoryName || item.addOnCategory || strings.venueConfig_addons_uncategorized;

export const CategorizedAddonSelector: FC<CategorizedAddonSelectorProps> = ({
  addOnItems,
  selectedIds,
  onChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeCategoryCode, setActiveCategoryCode] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const categories = useMemo(() => {
    const map = new Map<string, CategoryGroup>();
    addOnItems.forEach(item => {
      const code = getCategoryKey(item);
      if (!map.has(code)) {
        map.set(code, {
          code,
          name: getCategoryLabel(item),
          items: [],
        });
      }
      map.get(code)!.items.push(item);
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [addOnItems]);

  useEffect(() => {
    if (categories.length === 0) {
      setActiveCategoryCode('');
      return;
    }
    if (!categories.some(c => c.code === activeCategoryCode)) {
      setActiveCategoryCode(categories[0].code);
    }
  }, [categories, activeCategoryCode]);

  const activeCategory = categories.find(c => c.code === activeCategoryCode);

  const filteredAddons = useMemo(() => {
    if (!activeCategory) return [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return activeCategory.items;
    return activeCategory.items.filter(
      item =>
        item.name.toLowerCase().includes(term) ||
        getCategoryLabel(item).toLowerCase().includes(term),
    );
  }, [activeCategory, searchTerm]);

  const getCategorySelectionState = useCallback(
    (category: CategoryGroup) => {
      const ids = category.items.map(i => i.id);
      const selectedCount = ids.filter(id => selectedSet.has(id)).length;
      return {
        selectedCount,
        total: ids.length,
        allSelected: ids.length > 0 && selectedCount === ids.length,
        someSelected: selectedCount > 0 && selectedCount < ids.length,
      };
    },
    [selectedSet],
  );

  const toggleAddon = (id: number, checked: boolean) => {
    if (checked) {
      onChange([...selectedIds, id]);
    } else {
      onChange(selectedIds.filter(existing => existing !== id));
    }
  };

  const allAddonIds = useMemo(() => addOnItems.map(item => item.id), [addOnItems]);
  const allAddonsSelected =
    allAddonIds.length > 0 && allAddonIds.every(id => selectedSet.has(id));
  const someAddonsSelected = selectedIds.length > 0 && !allAddonsSelected;

  const toggleSelectAll = () => {
    if (allAddonsSelected) {
      onChange([]);
    } else {
      onChange([...allAddonIds]);
    }
  };

  const toggleCategory = (category: CategoryGroup) => {
    const { allSelected } = getCategorySelectionState(category);
    const categoryIds = category.items.map(i => i.id);
    if (allSelected) {
      onChange(selectedIds.filter(id => !categoryIds.includes(id)));
    } else {
      const merged = new Set([...selectedIds, ...categoryIds]);
      onChange(Array.from(merged));
    }
  };

  const selectionSummary = useMemo(() => {
    const parts = categories
      .map(cat => {
        const { selectedCount } = getCategorySelectionState(cat);
        return selectedCount > 0 ? `${cat.name} (${selectedCount})` : null;
      })
      .filter(Boolean);
    return parts;
  }, [categories, getCategorySelectionState]);

  if (addOnItems.length === 0) {
    return <VenueConfigEmptyState message={strings.venueConfig_addons_empty} />;
  }

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          px: 2,
          py: 1.5,
          mb: 2,
          backgroundColor: selectedIds.length > 0 ? 'primary.main' : 'grey.50',
          color: selectedIds.length > 0 ? 'primary.contrastText' : 'text.primary',
        }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
          <Typography variant="body2" fontWeight={600}>
            {selectedIds.length === 0
              ? strings.venueConfig_addons_none_selected
              : strings.venueConfig_addons_selected_count.replace('{count}', String(selectedIds.length))}
          </Typography>
          <FormControlLabel
            sx={{ m: 0, color: 'inherit' }}
            control={
              <Checkbox
                size="small"
                checked={allAddonsSelected}
                indeterminate={someAddonsSelected}
                onChange={toggleSelectAll}
                sx={{
                  color: 'inherit',
                  '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: 'inherit' },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: 'inherit' }}>
                {strings.venueConfig_addons_select_all}
              </Typography>
            }
          />
        </Box>
        {selectionSummary.length > 0 && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              opacity: 0.9,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {selectionSummary.join(' · ')}
          </Typography>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', minHeight: 360 }}>
        <Box
          sx={{
            width: isMobile ? '100%' : 240,
            flexShrink: 0,
            borderRight: isMobile ? 0 : 1,
            borderBottom: isMobile ? 1 : 0,
            borderColor: 'divider',
            maxHeight: isMobile ? 200 : 420,
            overflow: 'auto',
          }}>
          <Box sx={{ px: 2, py: 1.5, backgroundColor: 'grey.50' }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {strings.venueConfig_addons_categories}
            </Typography>
          </Box>
          <List dense disablePadding>
            {categories.map(category => {
              const { selectedCount, total, allSelected, someSelected } = getCategorySelectionState(category);
              const isActive = category.code === activeCategoryCode;
              return (
                <ListItem
                  key={category.code}
                  disablePadding
                  secondaryAction={
                    <Checkbox
                      edge="end"
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={() => toggleCategory(category)}
                      onClick={e => e.stopPropagation()}
                    />
                  }>
                  <ListItemButton selected={isActive} onClick={() => setActiveCategoryCode(category.code)}>
                    <ListItemText
                      primary={category.name}
                      secondary={`${selectedCount}/${total}`}
                      primaryTypographyProps={{ fontWeight: isActive ? 600 : 400, fontSize: '0.875rem' }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {activeCategory?.name ?? strings.venueConfig_addons_select_category}
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={strings.venueConfig_addons_search_placeholder}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')} aria-label="clear search">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            {activeCategory && (
              <FormControlLabel
                sx={{ mt: 1, ml: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={getCategorySelectionState(activeCategory).allSelected}
                    indeterminate={getCategorySelectionState(activeCategory).someSelected}
                    onChange={() => toggleCategory(activeCategory)}
                  />
                }
                label={
                  <Typography variant="body2">{strings.venueConfig_addons_select_entire_category}</Typography>
                }
              />
            )}
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', maxHeight: isMobile ? 280 : 340 }}>
            {filteredAddons.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                {strings.venueConfig_addons_no_results}
              </Typography>
            ) : (
              <List dense>
                {filteredAddons.map(item => (
                  <React.Fragment key={item.id}>
                    <ListItem disablePadding>
                      <ListItemButton onClick={() => toggleAddon(item.id, !selectedSet.has(item.id))} dense>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            size="small"
                            checked={selectedSet.has(item.id)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText primary={item.name} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                      </ListItemButton>
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default CategorizedAddonSelector;
